import { type ChangeEvent, useEffect, useRef, useState } from "react";
import {
    cropReset,
    cropSetCropBox,
    cropSetCustomRatioX,
    cropSetCustomRatioY,
    cropSetHeight,
    cropSetLeft,
    cropSetRatio,
    cropSetTop,
    cropSetWidth,
    deleteAllOperations,
    flipReset,
    rotateReset,
} from "store/features/bulkResize/actions";
import { useAppDispatch, useAppSelector } from "store/store";
import { getRatioValue } from "client/utils/getRatioValue";
import type Cropper from "cropperjs";
import { cropRatioKeys, CropRatioType } from "store/types/crop";
import { useDebounceFunc } from "client/hooks/utils/useDebounceFunc";
import { sleep } from "client/utils/sleep";
import { divide } from "client/utils/divide";
import { useBulkResizerImageProvider } from "../../BulkResizerImageProvider";

export const useBulkResizerCropSettings = (imageId: string) => {
    const image = useAppSelector((store) => store.bulkResize.images.find((image) => image.id === imageId));
    if (!image) throw new Error("image not found");
    const {
        crop: {
            width,
            height,
            ratio,
            customRatioValues: { x: customRatioX, y: customRatioY },
        },
    } = image;

    const dispatch = useAppDispatch();

    const [isResetting, setIsResetting] = useState(false);
    const cropButtonRef = useRef<HTMLDivElement>(null);
    const resetButtonRef = useRef<HTMLDivElement>(null);
    const { cropperRef } = useBulkResizerImageProvider();

    const handleReset = async () => {
        const cropper = cropperRef?.current;
        if (isResetting) return;
        if (!image || !image.crop.preview || !cropper) return;
        setIsResetting(true);
        await sleep();

        cropper.destroy();
        cropperRef.current = null;

        dispatch(cropSetCropBox({ imageId, cropBox: null }));
        dispatch(flipReset({ imageId }));
        dispatch(rotateReset({ imageId }));
        dispatch(deleteAllOperations({ imageId }));
        dispatch(cropReset({ imageId }));

        handleRatioChange("freeform");

        setIsResetting(false);

        document.body.dispatchEvent(new Event("crop-reset-complete"));
    };

    const handleToggleCropOrientation = () => {
        if (!image) return;

        cropperRef?.current?.setCropBoxData({
            width: getScaledDownWidth(height),
            height: getScaledDownHeight(width),
        });

        // because it is flipped
        if (height / width === image.original.width / image.original.height) {
            handleRatioChange("original");
        } else {
            const sameRatio = cropRatioKeys
                .filter((r) => r !== "custom" && r !== "original" && r !== "freeform")
                .find((r) => getRatioValue(r) === height / width);
            sameRatio ? handleRatioChange(sameRatio) : handleRatioChange("custom");
        }
    };

    const getScaledDownWidth = (width: number): number => {
        if (!image || !image.crop.preview) return width;

        const widthRatio = image.original.width / image.crop.preview.width;
        if (widthRatio === 0) return width;

        return width / widthRatio;
    };

    const getScaledUpWidth = (width: number): number => {
        if (!image || !image.crop.preview) return width;

        const widthRatio = image.original.width / image.crop.preview.width;
        if (widthRatio === 0) return width;

        return width * widthRatio;
    };

    const getScaledDownHeight = (height: number): number => {
        if (!image || !image.crop.preview) return height;

        const heightRatio = image.original.height / image.crop.preview.height;

        return height / heightRatio;
    };

    const getScaledUpHeight = (height: number): number => {
        if (!image || !image.crop.preview) return height;

        const heightRatio = image.original.height / image.crop.preview.height;

        return height * heightRatio;
    };

    const handleRatioChange = (newRatio: CropRatioType) => {
        const cropper = cropperRef?.current;
        if (!cropper || !image || !image.crop.preview) return;

        const prevCropBox = cropper.getCropBoxData();

        dispatch(cropSetRatio({ imageId, value: newRatio }));

        if (newRatio === "original") {
            cropper.setAspectRatio(image.crop.preview.width / image.crop.preview.height);
        } else if (newRatio !== "custom") {
            cropper.setAspectRatio(getRatioValue(newRatio));
        } else if (newRatio === "custom") {
            cropper.setAspectRatio(1);
        }

        handleCropperRatioChangeSideEffect(cropper, prevCropBox);
    };

    const handleCropperRatioChangeSideEffect = (cropper: Cropper, prevCropBox: Cropper.CropBoxData) => {
        const { width: newWidth, height: newHeight } = cropper.getCropBoxData();
        cropper.setCropBoxData({
            ...prevCropBox,
            width: newWidth,
            height: newHeight,
        });
        dispatch(cropSetWidth({ imageId, value: getScaledUpWidth(newWidth) }));
        dispatch(cropSetHeight({ imageId, value: getScaledUpHeight(newHeight) }));
    };

    const handleHeightChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!image) return;
        const __height = Number(e.target.value);
        const _height = __height > 0 ? (__height > image.original.height ? image.original.height : __height) : 0;

        if (ratio !== "freeform") {
            let _width = width;
            if (ratio === "original") {
                _width = (image.original.width / image.original.height) * _height;
            } else if (ratio === "custom") {
                _width = divide(customRatioX, customRatioY) * _height;
            } else {
                _width = _height * getRatioValue(ratio);
            }

            dispatch(cropSetWidth({ imageId, value: _width }));
        }

        dispatch(cropSetHeight({ imageId, value: _height }));

        cropperRef?.current?.setCropBoxData({
            height: getScaledDownHeight(_height),
        });
    };

    const handleWidthChange = (e: ChangeEvent<HTMLInputElement>) => {
        if (!image) return;
        const __width = Number(e.target.value);
        const _width = __width > 0 ? (__width > image.original.width ? image.original.width : __width) : 0;

        if (ratio !== "freeform") {
            let _height = height;
            if (ratio === "original") {
                _height = (image.original.height / image.original.width) * _width;
            } else if (ratio === "custom") {
                _height = divide(customRatioY, customRatioX) * _width;
            } else {
                _height = _width / getRatioValue(ratio);
            }
            dispatch(cropSetHeight({ imageId, value: _height }));
        }

        dispatch(cropSetWidth({ imageId, value: _width }));

        cropperRef?.current?.setCropBoxData({
            width: getScaledDownWidth(_width),
        });
    };

    const handleTopChange = (e: ChangeEvent<HTMLInputElement>) => {
        const cropper = cropperRef?.current;
        if (!cropper) return;
        cropper.setCropBoxData({
            top: getScaledDownHeight(+e.target.value),
        });
        const top = getScaledUpWidth(cropper.getCropBoxData().top);
        dispatch(cropSetTop({ imageId, value: top }));
    };

    const handleLeftChange = (e: ChangeEvent<HTMLInputElement>) => {
        const cropper = cropperRef?.current;
        if (!cropper) return;
        cropper.setCropBoxData({
            left: getScaledDownWidth(+e.target.value),
        });
        const left = getScaledUpWidth(cropper.getCropBoxData().left);
        dispatch(cropSetLeft({ imageId, value: left }));
    };

    const cropDebounce = useDebounceFunc();
    const handleCropApplyEvent = () => {
        cropDebounce(() => {
            cropButtonRef.current?.click();
        });
    };

    const handleCropResetEvent = () => {
        cropDebounce(() => {
            resetButtonRef.current?.click();
        });
    };

    const handleCustomRatioXChange = (value: number) => {
        dispatch(cropSetCustomRatioX({ imageId, value }));
        handleApplyCustomRatio(value, customRatioY);
    };

    const handleCustomRatioYChange = (value: number) => {
        dispatch(cropSetCustomRatioY({ imageId, value }));
        handleApplyCustomRatio(customRatioX, value);
    };

    const handleApplyCustomRatio = (x: number, y: number) => {
        const cropper = cropperRef?.current;
        if (x <= 0 || y <= 0 || !cropper) return;
        const prevCropBox = cropper.getCropBoxData();
        cropper.setAspectRatio(x / y);
        handleCropperRatioChangeSideEffect(cropper, prevCropBox);
    };

    useEffect(() => {
        document.body.addEventListener("crop-apply", handleCropApplyEvent);
        document.body.addEventListener("crop-reset", handleCropResetEvent);

        return () => {
            const cropper = cropperRef?.current;
            if (!cropper) return;
            cropper?.destroy();
            cropperRef.current = null;

            document.body.removeEventListener("crop-apply", handleCropApplyEvent);
            document.body.removeEventListener("crop-reset", handleCropResetEvent);
        };
    }, []);

    return {
        handleReset,
        handleToggleCropOrientation,
        ratio,
        handleRatioChange,
        width,
        height,
        handleWidthChange,
        handleHeightChange,
        handleTopChange,
        handleLeftChange,
        isResetting,
        cropButtonRef,
        resetButtonRef,
        customRatioX,
        customRatioY,
        handleCustomRatioXChange,
        handleCustomRatioYChange,
    };
};
